import { supabase } from './client';
import type { Job } from '../../types/jobs';

export interface SupabaseJob {
  id: string;
  title: string;
  location: string;
  pay_rate: string;
  description: string;
  type: string;
  farmer_id: string;
  farmer_name: string;
  requirements: string[];
  status: string;
  housing: string;
  photo_url?: string;
  voice_message_url?: string;
  audio_message_url?: string;
  is_completed?: boolean;
  contact_email?: string;
  created_at: string;
}

function convertSupabaseToJob(supabaseJob: SupabaseJob): Job {
  return {
    id: supabaseJob.id,
    title: supabaseJob.title,
    location: supabaseJob.location,
    payRate: supabaseJob.pay_rate === 'DOE' ? 'DOE' : Number(supabaseJob.pay_rate),
    description: supabaseJob.description,
    type: supabaseJob.type as Job['type'],
    farmerId: supabaseJob.farmer_id,
    farmerName: supabaseJob.farmer_name,
    requirements: supabaseJob.requirements || [],
    status: supabaseJob.status as Job['status'],
    housing: supabaseJob.housing as Job['housing'],
    photoUrl: supabaseJob.photo_url,
    isCompleted: supabaseJob.is_completed || false,
    audioMessageUrl: supabaseJob.audio_message_url,
    contactEmail: supabaseJob.contact_email,
    createdAt: supabaseJob.created_at
  };
}

export async function addJob(jobData: Omit<Job, 'id'>) {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    const supabaseJobData = {
      title: jobData.title,
      location: jobData.location,
      pay_rate: jobData.payRate.toString(),
      description: jobData.description,
      type: jobData.type,
      farmer_id: user.id,
      farmer_name: jobData.farmerName,
      requirements: jobData.requirements || [],
      status: jobData.status,
      housing: jobData.housing,
      photo_url: jobData.photoUrl || null,
      audio_message_url: jobData.audioMessageUrl || null,
      contact_email: jobData.contactEmail || null,
      load_id: null, // Explicitly set to null for regular jobs
      commodity_type: null // Explicitly set to null for regular jobs
    };

    const { data, error } = await supabase
      .from('jobs')
      .insert(supabaseJobData)
      .select()
      .single();

    if (error) throw error;

    await notifyJobSubscribers({
      jobId: data.id,
      jobTitle: data.title,
      jobLocation: data.location,
      jobPayRate: data.pay_rate,
      jobType: data.type,
      jobDescription: data.description,
      employerName: data.farmer_name,
      createdAt: data.created_at || new Date().toISOString(),
    });

    await createInAppJobAlerts(data.id, data.title, data.location, data.description);

    return { id: data.id, error: null };
  } catch (error) {
    console.error('Error adding job to Supabase:', error);
    return { id: null, error: error as Error };
  }
}

async function notifyJobSubscribers(jobData: {
  jobId: string;
  jobTitle: string;
  jobLocation: string;
  jobPayRate: string;
  jobType: string;
  jobDescription: string;
  employerName: string;
  createdAt: string;
}) {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !anonKey) {
      console.error('Missing Supabase credentials');
      return;
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/notify-job-subscribers`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${anonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(jobData),
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('Failed to notify subscribers:', error);
    } else {
      const result = await response.json();
      console.log('Notification result:', result);
    }
  } catch (error) {
    console.error('Error notifying subscribers:', error);
  }
}

export async function getSupabaseJobs() {
  try {
    // Check if we're online
    if (!navigator.onLine) {
      throw new Error('No internet connection');
    }

    // Add timeout and retry logic  
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
    
    const { data, error } = await supabase
      .from('jobs')
      .select('*')
      .is('load_id', null)
      .order('created_at', { ascending: false })
      .abortSignal(controller.signal);
    
    clearTimeout(timeoutId);

    if (error) throw error;
    
    const jobs = (data || []).map(convertSupabaseToJob);
    return { jobs, error: null };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.error('Supabase request timed out');
      return { jobs: [], error: new Error('Request timed out - please try again') };
    }
    
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      console.error('Network error connecting to Supabase');
      return { jobs: [], error: new Error('Unable to connect to server - please check your internet connection') };
    }
    
    console.error('Error getting Supabase jobs:', error);
    return { jobs: [], error: error instanceof Error ? error : new Error('Unknown error occurred') };
  }
}

export async function getSupabaseJob(jobId: string) {
  try {
    const { data, error } = await supabase
      .from('jobs')
      .select('*')
      .eq('id', jobId)
      .single();

    if (error) throw error;
    
    const job = convertSupabaseToJob(data);
    return { job, error: null };
  } catch (error) {
    console.error('Error getting Supabase job:', error);
    return { job: null, error: error as Error };
  }
}

export async function deleteSupabaseJob(jobId: string) {
  try {
    const { error } = await supabase
      .from('jobs')
      .delete()
      .eq('id', jobId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error deleting Supabase job:', error);
    return { error: error as Error };
  }
}

export async function toggleJobCompletion(jobId: string, isCompleted: boolean) {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('jobs')
      .update({ is_completed: isCompleted })
      .eq('id', jobId)
      .eq('farmer_id', user.id);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error toggling job completion:', error);
    return { error: error as Error };
  }
}

export const deleteJob = deleteSupabaseJob;

async function createInAppJobAlerts(
  jobId: string,
  jobTitle: string,
  jobLocation: string,
  jobDescription: string
) {
  try {
    const { data: subscriptions, error } = await supabase
      .from('job_subscriptions')
      .select('user_id, keywords, location')
      .eq('is_active', true);

    if (error || !subscriptions?.length) return;

    const searchText = `${jobTitle} ${jobDescription}`.toLowerCase();
    const notifications: { user_id: string; title: string; message: string; type: string; reference_id: string; reference_type: string }[] = [];
    const notifiedUserIds = new Set<string>();

    for (const sub of subscriptions) {
      if (notifiedUserIds.has(sub.user_id)) continue;

      const locationMatch =
        !sub.location || sub.location.toLowerCase() === 'any' || jobLocation.toLowerCase().includes(sub.location.toLowerCase());

      if (!locationMatch) continue;

      const matchedKeyword = (sub.keywords || []).find((kw: string) =>
        searchText.includes(kw.toLowerCase())
      );

      if (matchedKeyword) {
        notifiedUserIds.add(sub.user_id);
        notifications.push({
          user_id: sub.user_id,
          title: 'New Job Match',
          message: `A new job matching your alert "${matchedKeyword}" was posted: "${jobTitle}" in ${jobLocation}`,
          type: 'job_alert',
          reference_id: jobId,
          reference_type: 'job',
        });
      }
    }

    if (notifications.length > 0) {
      await supabase.from('notifications').insert(notifications);
    }
  } catch (err) {
    console.warn('Failed to create in-app job alerts:', err);
  }
}